import React from "react";
import { Table, Tag, Button, Tooltip, Modal, Space } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  StopOutlined,
} from "@ant-design/icons";
import FinanceService from "../../../dependencies/custom/financeService";

const FeeStructuresTable = ({
  feeStructures,
  loading,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  onView,
}) => {
  const handleDelete = (feeStructure) => {
    Modal.confirm({
      title: "Delete Fee Structure",
      content: `Are you sure you want to delete "${feeStructure.name}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      onOk: () => onDelete(feeStructure.feeStructureId),
    });
  };

  const handleActivate = (feeStructure) => {
    Modal.confirm({
      title: "Activate Fee Structure",
      content: (
        <div>
          <p>
            Activating <strong>"{feeStructure.name}"</strong> will:
          </p>
          <ul>
            <li>Automatically assign this fee to all matching students</li>
            <li>
              Create fee assignments based on class, term, academic year, and
              day/boarding type
            </li>
            <li>Make it visible in student bills</li>
          </ul>
          <p>Do you want to continue?</p>
        </div>
      ),
      okText: "Activate & Assign",
      okType: "primary",
      onOk: () => onActivate(feeStructure.feeStructureId),
    });
  };

  const handleDeactivate = (feeStructure) => {
    Modal.confirm({
      title: "Deactivate Fee Structure",
      content: (
        <div>
          <p>
            Deactivating <strong>"{feeStructure.name}"</strong> will:
          </p>
          <ul>
            <li>Remove unpaid assignments from students</li>
            <li>Hide unpaid charges from student bills</li>
            <li>
              Stop this fee from being auto-assigned or collected going forward
            </li>
          </ul>
          <p>
            <strong>Payment records are kept:</strong> paid or partially paid
            assignments will remain for finance history.
          </p>
          <p>Do you want to continue?</p>
        </div>
      ),
      okText: "Deactivate",
      okType: "danger",
      onOk: () => onDeactivate(feeStructure.feeStructureId),
    });
  };

  const columns = [
    {
      title: "Fee Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => <a onClick={() => onEdit(record)}>{text}</a>,
    },
    {
      title: "Fee Type",
      dataIndex: "feeTypeName",
      key: "feeTypeName",
      render: (text, record) => (
        <div>
          <div>{text || "N/A"}</div>
          <small style={{ color: "#888" }}>{record.feeTypeCode}</small>
        </div>
      ),
    },
    {
      title: "Class",
      dataIndex: "schoolClassName",
      key: "schoolClassName",
      render: (text) => text || "All Classes",
    },
    {
      title: "Term",
      dataIndex: "termName",
      key: "termName",
      render: (text) => text || "Annual",
    },
    {
      title: "Day/Boarding",
      dataIndex: "boardingType",
      key: "boardingType",
      render: (type) => {
        const colors = {
          Day: "cyan",
          Boarding: "purple",
          Both: "blue",
        };
        return <Tag color={colors[type] || "default"}>{type || "Both"}</Tag>;
      },
    },
    {
      title: "Academic Year",
      dataIndex: "acadyr",
      key: "acadyr",
      render: (year) => <Tag color="cyan">{year}</Tag>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (amount) => FinanceService.formatCurrency(parseFloat(amount)),
    },
    {
      title: "Mandatory",
      dataIndex: "isMandatory",
      key: "isMandatory",
      align: "center",
      render: (isMandatory) =>
        isMandatory ? (
          <Tag color="red">Required</Tag>
        ) : (
          <Tag color="default">Optional</Tag>
        ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Tag color={isActive === 1 ? "green" : "orange"}>
          {isActive === 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onView && onView(record)}
            />
          </Tooltip>
          {record.isActive === 1 ? (
            <Tooltip title="Deactivate and remove unpaid assignments">
              <Button
                type="link"
                icon={<StopOutlined />}
                onClick={() => handleDeactivate(record)}
                style={{ color: "#ff4d4f" }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Activate & Assign to Students">
              <Button
                type="link"
                icon={<ThunderboltOutlined />}
                onClick={() => handleActivate(record)}
                style={{ color: "#52c41a" }}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={feeStructures}
      rowKey="feeStructureId"
      loading={loading}
      scroll={{ x: 1200 }}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} fee structures`,
        responsive: true,
      }}
    />
  );
};

export default FeeStructuresTable;
